import {OTOMATIK_GOREV_ISLEME_ANAHTARI} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisi";

/**
 * Otomatik görev işleme: AÇILIŞ VARSAYILANI ve kullanıcının AÇIK/KAPALI seçimi.
 *
 * KARAR (20.10.2026): kullanıcıların kurduğu otomatik (zamanlanmış) sorgular
 * UYAP'ın yoğun istek kısıtlaması yüzünden canlıya KAPALI çıkıyor. Bot
 * canlıdaki gibi açılışta 'dur' başlar; kullanıcı Electron Ayarlar menüsündeki
 * "Otomatik görev işleme" anahtarından açar ya da İşlemler'den Başlat'a basar.
 * Bu karar yalnız botun KENDİLİĞİNDEN BAŞLAMASINI kapatır: elle başlatılan
 * görevler, tekil sorgular ve snapshot yazımı aynen çalışır.
 *
 * GERİ AÇMAK: UYAP'la anlaşılırsa, hiç seçim yapmamış herkes için açılış davranışını
 * çevirmek TEK satırdır (`ACILIS_VARSAYILAN_OTOMATIK_ISLEME = true`); bilinçli
 * kapatanlar kapalı kalır. Sunucu tarafı bayrağı ayrıdır (Laravel
 * `SORGU_SENKRON_AKTIF`). Bunun doğru çalışması için açılış değeri plugin'in
 * localStorage kaydından DEĞİL, buradaki iki kaynaktan türetilir:
 *
 *   etkin = kullanıcı seçimi (varsa)  ??  ACILIS_VARSAYILAN_OTOMATIK_ISLEME
 *
 * Plugin kaydı (`otomatikGorevIsleme`) türetilmiş değerin yansımasıdır ve HER
 * açılışta `etkin` ile yeniden yazılır. Bu şart: plugin `initAutoPlay` kayıt
 * yokken tohumu (false) KALICI yazar ve sonraki açılışlarda saklı değeri okur;
 * türetme olmasa, bugünkü sürümü bir kez açmış herkesin kaydında kalıcı `false`
 * kalır ve sabit `true` yapılsa bile hiçbiri kendiliğinden başlamazdı.
 *
 * KULLANICI SEÇİMİ (`otomatikGorevIslemeKullaniciSecimi`), yalnız Ayarlar
 * menüsünden yapılır ve iki yönlüdür: "1" = açtı, "0" = kapattı (onaylı uyarıdan
 * geçerek), anahtar yok = hiç seçmedi. Böylece:
 *  - hiç seçmemiş kullanıcı sabiti izler (sabit değişince onun davranışı da değişir),
 *  - açan/kapatan kullanıcının bilinçli tercihi sabitten bağımsız korunur,
 *  - Beta'da DEV-3157 tohumuyla `true` kalmış kayıt (işaretsiz) seçim sayılmaz ve
 *    ilk açılışta sabite göre yeniden yazılır (şimdi: kapalı).
 *
 * Kabuk (electron-imza-app `main.js`) menü işaretini kendi varsayılanıyla kurar ve
 * renderer gerçek durumu bildirince düzeltir (`App.vue` bunu rota geçişinden önce
 * yapar); sabit çevrilirse kabuk varsayılanı da aynı yönde güncellenmelidir, aksi
 * halde menü yalnız ilk saniyelerde yanlış gösterir.
 *
 * Plugin anahtarının adı değiştirilmedi: aynı plugin icrapro-app'te de bu anahtarı
 * ("işlem oluşunca başlat") kullanıyor (ayrı origin/localStorage, ama aynı ad);
 * yeniden adlandırmak oradaki tercihleri de sıfırlardı.
 */

/**
 * Açılış varsayılanı: kullanıcı hiç seçim yapmadıysa botun açılıştaki durumu.
 * `true` yapmak, seçim yapmamış herkes için botu açılışta kendiliğinden başlatır.
 */
export const ACILIS_VARSAYILAN_OTOMATIK_ISLEME = false;

/** Kullanıcının Ayarlar menüsünden yaptığı seçimin localStorage anahtarı ("1" açık, "0" kapalı). */
export const KULLANICI_SECIMI_ANAHTARI = "otomatikGorevIslemeKullaniciSecimi";

/** localStorage'ın bu dosyanın kullandığı dar yüzü — testte sahtelenir. */
export interface TercihDeposu {
    getItem(anahtar: string): string | null;
    setItem(anahtar: string, deger: string): void;
    removeItem(anahtar: string): void;
}

/**
 * Kullanıcının Ayarlar menüsünden yaptığı son seçim.
 *
 * @returns `true` açtı, `false` kapattı, `null` hiç seçmedi (ya da kayıt okunamadı/bozuk).
 */
export function kullaniciSeciminiOku(depo?: TercihDeposu): boolean | null {
    try {
        const d = depo ?? localStorage;
        const kayit = d.getItem(KULLANICI_SECIMI_ANAHTARI);
        if (kayit === "1") {
            return true;
        }
        if (kayit === "0") {
            return false;
        }
        return null;
    } catch (e) {
        // Erişilemeyen depo: seçim yok sayılır, açılış varsayılana düşer.
        return null;
    }
}

/**
 * Ayarlar menüsünden yapılan seçimi kaydeder ("1" açık, "0" kapalı). Kalıcı tercihin
 * kendisi (`updateAutoPlay`) ayrıca yazılır.
 */
export function kullaniciSeciminiKaydet(acik: boolean, depo?: TercihDeposu): void {
    try {
        const d = depo ?? localStorage;
        d.setItem(KULLANICI_SECIMI_ANAHTARI, acik === true ? "1" : "0");
    } catch (e) {
        // Seçim yazılamazsa bir sonraki açılışta varsayılan geçerli olur
        // (varsayılan kapalıyken güvenli taraf); akış bozulmaz.
    }
}

/**
 * Açılışta uygulanacak tercihi türetir ve plugin kaydına yazar.
 * `initAutoPlay` commit'inden ÖNCE çağrılır; plugin böylece türetilmiş değeri okur.
 *
 * @param varsayilan Kullanıcı seçimi yokken geçerli değer; testte sabitin başka
 *                   değeri simüle edilir.
 * @returns Uygulanan (etkin) açılış tercihi.
 */
export function acilisTercihiniUygula(
    varsayilan: boolean = ACILIS_VARSAYILAN_OTOMATIK_ISLEME,
    depo?: TercihDeposu
): boolean {
    const etkin = kullaniciSeciminiOku(depo) ?? varsayilan;
    try {
        const d = depo ?? localStorage;
        d.setItem(OTOMATIK_GOREV_ISLEME_ANAHTARI, JSON.stringify(etkin));
    } catch (e) {
        // Yazılamazsa `initAutoPlay` kendi hata yolunda tohuma (varsayılan) düşer.
    }
    return etkin;
}
